/* eslint-disable react/no-multi-comp */
import { useState } from 'react';
import Image from 'next/image';

import { TimeService } from '@/utils/services/TimeService';

import { PostProvider, usePost } from '@/utils/hooks/context/usePost';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { rgbDataURL } from '@/utils/blurImage';
import { classNames } from '@/utils/classNames';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

import {
    getFooterStats,
    getNumberFormatOptions,
    getStatIcon,
    getStatLabel
} from '@/lib/posts/postFooterStats';
import { POST, SQUARE, STORY } from '@/lib/posts/postTypes';
import { INSTAGRAM, LINKEDIN, YOUTUBE } from '@/lib/socialProfile';

import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import SocialProfileMiniDisplay from '@/components/composite/SocialProfiles/SocialProfileMiniDisplay';

const Post = ({
    data,
    isLoading = false,
    isExport = false,
    config = {
        postFormat: SQUARE,
        postType: POST,
        activeFilter: 'most_recent_post',
        network: INSTAGRAM,
        numberFormat: 'comma'
    },
    className,
    children,
    ...restProps
}) => {
    const [referenceElement, setReferenceElement] = useState();

    return (
        <PostProvider defaultValues={{ data, isLoading, config, isExport }}>
            <div
                className={classNames(
                    'relative flex flex-col items-center rounded-md quantum:border quantum:border-icoGray-200',
                    { [config.postType]: isLoading },
                    [className]
                )}
                ref={setReferenceElement}
                {...restProps}>
                {typeof children === 'function' ? children({ referenceElement }) : children}
            </div>
        </PostProvider>
    );
};
Post.propTypes = {
    data: PropTypes.object,
    isLoading: PropTypes.bool,
    config: PropTypes.object,
    children: PropTypes.any,
    isExport: PropTypes.bool,
    className: PropTypes.string
};

// bg color for post type
const bgColorPostType = {
    text: 'bg-icoBlue-500 text-icoBlue-800 dark:bg-icoBlue-800 dark:text-icoBlue-500',
    status: 'bg-icoBlue-500 text-icoBlue-800 dark:bg-icoBlue-800 dark:text-icoBlue-500',
    retweet: 'bg-icoPurple-500 text-icoPurple-800 dark:bg-icoPurple-800 dark:text-icoPurple-500',
    quote: 'bg-icoOrange-500 text-icoOrange-800 dark:bg-icoOrange-800 dark:text-icoOrange-500',
    document: 'bg-icoPink-500 text-icoPink-800 dark:bg-icoPink-800 dark:text-icoPink-500',
    link: 'bg-icoGreen-500 text-icoGreen-800 dark:bg-icoGreen-800 dark:text-icoGreen-500',
    poll: 'bg-icoAquaBlue-500 text-icoAquaBlue-800 dark:bg-icoAquaBlue-800 dark:text-icoAquaBlue-500'
};

//Content Wrapper
const Content = ({ children, className }) => {
    return (
        <div
            aria-label="post-content"
            className={classNames('group relative overflow-hidden rounded-md', className)}
            data-testid="post-content">
            {children}
        </div>
    );
};

Content.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string
};

Post.Content = Content;

const MediaTags = ({ children }) => (
    <div className="absolute inset-x-0 bottom-0 z-1 flex items-end gap-0.5 bg-gradient-to-t from-icoBlack/40 to-transparent px-1 pb-1 pt-6">
        {children}
    </div>
);

MediaTags.propTypes = {
    children: PropTypes.node
};

Post.MediaTags = MediaTags;

const Collaborators = () => {
    const { t } = useTranslation();

    return (
        <span className="flex items-center gap-0.5 rounded-full bg-white px-1.5 py-1 dark:bg-icoDarkMode-anthracite">
            <Icon
                icon="multiprofiles"
                className="text-[10px] text-icoBlack dark:text-white"
                aria-label="collaborators"
            />
            <span className="font-GSemiBold text-[9px] uppercase text-icoBlack dark:text-white">
                {t('analytics:collab')}
            </span>
        </span>
    );
};
Post.Collaborators = Collaborators;

const Promoted = () => {
    const { t } = useTranslation();

    return (
        <span className="flex items-center gap-0.5 rounded-full bg-white px-1.5 py-1 dark:bg-icoDarkMode-anthracite">
            <Icon
                icon="paid-data"
                className="text-[10px] text-icoBlack dark:text-white"
                aria-label="promoted"
            />
            <span className="font-GSemiBold text-[9px] uppercase text-icoBlack dark:text-white">
                {t('analytics:paid_data')}
            </span>
        </span>
    );
};

Post.Promoted = Promoted;

const PostTypeIcon = ({ type, className }) => {
    return (
        <Icon
            aria-label="type-post"
            icon={type}
            className={classNames(
                'absolute top-[.5rem] left-[.5rem] z-1 rounded-md bg-icoBlack p-1.5 text-xxs text-white dark:bg-icoDarkMode-deepBlack',
                className
            )}
        />
    );
};

PostTypeIcon.propTypes = {
    type: PropTypes.string.isRequired
};

Post.PostTypeIcon = PostTypeIcon;

const statusStyle = {
    default: 'bg-icoGray-100 leading-6 font-GMedium pt-5 px-5',
    album: 'bg-white leading-4 font-GMedium pt-5 px-5'
};

const statusStyleDark = {
    default: 'dark:bg-icoDarkMode-deepBlack dark:text-white',
    album: 'dark:bg-icoDarkMode-anthracite dark:text-white'
};

const Status = ({ formattedCaption, withMedia = true, remoteMedia = false, style = 'default' }) => {
    const { data, isExport } = usePost();
    const bgColorPostTypeClass = bgColorPostType[data.type];
    
    return (
        <div>
            <div className="square">
                <div
                    className={classNames('relative break-words', statusStyle[style], {
                        [statusStyleDark[style]]: !isExport
                    }, [bgColorPostTypeClass])}>
                    {formattedCaption}
                </div>
            </div>
            {data?.medias?.length > 0 &&
            (data?.medias?.[0]?.image || data?.medias?.[0]?.thumbnail) &&
            withMedia ? (
                <div
                    className={classNames(
                        'absolute bottom-2 right-2 z-1 h-15 w-15 rounded-md bg-white p-0.5',
                        {
                            'shadow-icoS dark:bg-icoDarkMode-goudron': !isExport
                        }
                    )}>
                    <div className="relative h-full w-full overflow-hidden rounded">
                        <Img remoteMedia={remoteMedia} />
                    </div>
                </div>
            ) : null}
            <span
                className={classNames(
                    'absolute bottom-0 left-0 right-0 block h-1/3 w-full bg-gradient-to-b from-icoGray-100/0 to-icoGray-100/100',
                    {
                        'dark:from-icoDarkMode-deepBlack/0 dark:to-icoDarkMode-deepBlack/100':
                            !isExport
                    }
                )}
            />
        </div>
    );
};

Status.propTypes = {
    formattedCaption: PropTypes.node,
    withMedia: PropTypes.bool,
    remoteMedia: PropTypes.bool,
    style: PropTypes.oneOf(['default', 'album'])
};
Post.Status = Status;

//Post Image
const Img = ({ remoteMedia = false, imgFit = 'cover' }) => {
    const { isDarkMode } = useDarkMode();
    const { data, isExport } = usePost();
    const { t } = useTranslation();
    const [error, setError] = useState(false);

    if (
        error ||
        (!data?.medias?.[0]?.image && !data?.medias?.[0]?.thumbnail && !data?.medias?.[0]?.video)
    ) {
        return (
            <div
                className={classNames(
                    'flex h-full w-full flex-1 flex-col items-center justify-center space-y-4 bg-icoGray-100 text-icoGray-500',
                    { 'dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf': !isExport }
                )}>
                {/* Change Icon to real fallback */}

                <span
                    className={classNames(
                        'flex h-12 w-12 items-center justify-center rounded-full bg-icoGray-300',
                        { 'dark:bg-icoDarkMode-anthracite': !isExport }
                    )}>
                    <Icon
                        icon={data?.type === 'video' || data?.type === 'short' ? 'play' : 'albums'}
                    />
                </span>
                <p className="text-xs">
                    {data?.type === 'video'
                        ? t('commons:video_not_loaded')
                        : t('commons:media_not_loaded')}
                </p>
            </div>
        );
    }

    if (data?.medias?.[0]?.video && !data?.medias?.[0]?.thumbnail) {
        return (
            <video
                src={data?.medias?.[0]?.video}
                preload="metadata"
                className="min-h-full min-w-full"
                onError={() => setError(true)}
            />
        );
    }

    return remoteMedia ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={data?.medias?.[0]?.image ?? data?.medias?.[0]?.thumbnail}
            alt={`${data?.type} post`}
            className={`object-${imgFit} h-full w-full`}
            onError={() => setError(true)}
        />
    ) : (
        <Image
            src={data?.medias?.[0]?.image ?? data?.medias?.[0]?.thumbnail}
            alt={`${data?.type} post`}
            className="h-full w-full object-cover"
            fill
            sizes="(max-width: 350px) 100vw,
            max-width: 768px) 50vw,
              20vw"
            placeholder="blur"
            blurDataURL={isDarkMode ? rgbDataURL(5, 9, 22) : rgbDataURL(240, 242, 246)}
            priority={isExport}
            onError={() => setError(true)}
        />
    );
};

Img.propTypes = {
    remoteMedia: PropTypes.bool,
    imgFit: PropTypes.oneOf(['cover', 'contain'])
};

Post.Image = Img;

//Footer section with stats
// abstract to make number formatting easier
const StatNumber = ({ data, entry, numberFormat }) => {
    const formatOptions = getNumberFormatOptions(entry);
    const { isExport } = usePost();

    const { formatNumber } = useFormatNumber(numberFormat);
    return (
        <span className={classNames('text-xs text-icoGray-500 dark:text-white', { 'text-icoBlack': isExport })}>
            {(data || data === 0) && data !== null ? formatNumber(data, formatOptions) : 'N/A'}
        </span>
    );
};

StatNumber.propTypes = {
    data: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    entry: PropTypes.string,
    numberFormat: PropTypes.string
};

const Footer = ({ type, feedType = '', analytics = null, isLoadingAnalytics = false }) => {
    const { data, config } = usePost();
    const { t } = useTranslation('commons');
    const getStats = () => {
        if (feedType === 'feed') {
            if (config?.network === INSTAGRAM) {
                return ['likes', 'comments', 'engagements'];
            }

            return ['reactions', 'comments', 'engagements'];
        }

        return getFooterStats({
            postType: config?.postType,
            type: type,
            filter: config?.activeFilter,
            network: config?.network
        });
    };
    const stats = getStats();

    // Use provided analytics if available, otherwise fall back to data
    const getStatValue = (stat) => {
        return analytics?.[stat] ?? data?.[stat];
    };

    const getTooltipContent = (stat, network) => {
        if (network === LINKEDIN && stat === 'likes') {
            return t('post_footer.reactions');
        }
        return t(`post_footer.${stat}`);
    };

    return (
        <div
            aria-label="stats-section"
            className={classNames(
                'flex min-h-[3.125rem] flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-0.5 py-1',
                {
                    '!gap-5': config?.network === YOUTUBE
                }
            )}>
            {stats?.map((stat, i) => {
                const statValue = getStatValue(stat);
                const tooltipContent = getTooltipContent(stat, config?.network);
                if (!statValue && stat === 'engagement_rate') {
                    return null;
                } else {
                    const statLabel = getStatLabel(stat, config?.network);
                    const isStatLoading =
                        isLoadingAnalytics && (analytics === null || analytics[stat] === undefined);
                    return (
                        <Tooltip key={i} content={tooltipContent} placement="top" arrow={false}>
                            <span className="flex items-center space-x-2">
                                <span className="flex items-center gap-1">
                                    <Icon
                                        icon={getStatIcon(stat, config?.network)}
                                        className="text-sm text-icoBlack dark:text-white"
                                    />
                                    {statLabel && (
                                        <span className="font-GSemiBold text-xs text-icoGray-500 dark:text-white">
                                            {t(`post_footer.${statLabel}`)}
                                        </span>
                                    )}
                                </span>
                                {isStatLoading ? (
                                    <span className="h-3 w-8 animate-pulse rounded bg-icoGray-300 dark:bg-icoDarkMode-anthracite" />
                                ) : (
                                    <StatNumber
                                        data={statValue}
                                        entry={stat}
                                        numberFormat={config?.numberFormat}
                                    />
                                )}
                            </span>
                        </Tooltip>
                    );
                }
            })}
        </div>
    );
};

Footer.propTypes = {
    type: PropTypes.string,
    feedType: PropTypes.string,
    analytics: PropTypes.object,
    isLoadingAnalytics: PropTypes.bool
};

Post.Footer = Footer;

// Hover section — must not be a <button> so album/label actions and the open-viewer control
// (also buttons) are not nested inside a button (invalid HTML; hydration + a11y issues in PDF).
const HoverWrapper = ({
    children,
    forceDisplay = false,
    openViewer = () => {
        /** */
    }
}) => {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openViewer();
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            className={classNames(
                'absolute inset-0 z-10 flex cursor-pointer flex-col items-center rounded border-0 bg-icoBlack/90 transition duration-300 ease-in-out',
                {
                    'opacity-0 group-hover:opacity-100': !forceDisplay
                }
            )}
            onClick={openViewer}
            onKeyDown={handleKeyDown}>
            {children}
        </div>
    );
};

HoverWrapper.propTypes = {
    forceDisplay: PropTypes.bool,
    openViewer: PropTypes.func,
    children: PropTypes.node
};

Post.Hover = HoverWrapper;

const Placeholder = ({ postType, isExport }) => (
    <div
        aria-label="post-placeholder"
        className={classNames(
            'relative flex w-full flex-col items-center rounded-md bg-white px-2 pb-12 pt-2 quantum:border quantum:border-icoGray-200',
            {
                post: postType === POST,
                story: postType === STORY,
                'dark:bg-icoDarkMode-anthracite': !isExport
            }
        )}
        data-testid="loading-placeholder">
        <div
            className={classNames('h-full w-full animate-pulse rounded-md bg-icoGray-300', {
                'dark:bg-icoDarkMode-deepBlack': !isExport
            })}
        />
    </div>
);

Placeholder.propTypes = {
    postType: PropTypes.oneOf([POST, STORY]),
    isExport: PropTypes.bool
};

Post.Placeholder = Placeholder;

// post header
const Header = ({ data, isExport = false, className }) => {
    const { user } = useUser();
    const { formatFromNow } = useFormatDateTime(
        user?.settings?.timezone,
        user?.settings?.platform_language
    );

    if (!data?.username && !data?.social_profile) {
        return null;
    }

    const slug = data.username || data.social_profile?.slug;

    return (
        <div
            aria-label="post-header"
            className={classNames(
                // ponytail: no overflow-hidden — DefaultAvatar network badge uses -bottom/-right and was clipped
                'group relative mb-2 flex flex-row items-center justify-between space-x-1 font-GSemiBold text-xs text-black',
                className,
                { 'dark:text-white': !isExport }
            )}>
            <div className="flex min-w-0 flex-row items-center space-x-1">
                {data.social_profile?.id ? (
                    <SocialProfileMiniDisplay socialProfile={data.social_profile} isExport={isExport} />
                ) : null}
                {slug ? <span className="truncate">@{slug}</span> : null}
            </div>
            {data?.created ? (
                <span className="text-nowrap text-icoGray-400">
                    {TimeService.formatPostHeaderDate(data.created, formatFromNow)}
                </span>
            ) : null}
        </div>
    );
};

Header.propTypes = {
    data: PropTypes.object,
    isExport: PropTypes.bool,
    className: PropTypes.string
};

Post.Header = Header;

export default Post;
